import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { GetAllBranchesAPI } from 'api/space';
import type { Branch } from 'store/space/type';
import { PostCommunityImage, PostCommunityPost } from 'api/community';
import { makeAuthorizationConfig } from 'utils';
import { useCookies } from 'react-cookie';
import { getDistanceFromLatLonInKm } from 'utils/geocoder';
import { Spinner } from 'components/common/Spinner';

interface IProps {
  closeModal: () => void;
}

const ModalAddNewFeed: React.FC<IProps> = ({ closeModal }: IProps) => {
  const [cookies] = useCookies();

  const [content, setContent] = useState('');
  const [images, setImages] = useState<{ id: number; image: string }[]>([]);

  const [branches, setBranches] = useState<Branch[]>([]);
  const [selectedBranch, setSelectedBranch] = useState<Branch>(null);
  const [isSelectBoxShown, setSelectBoxShown] = useState(false);
  const [isLoading, setLoading] = useState(true);

  const checkLimit = e => {
    if (images.length >= 5) {
      alert('사진 업로드는 최대 5장까지만 가능합니다.');
      e.preventDefault();
    }
  };

  const handleImageUpload = async e => {
    const { files } = e.target;
    let limit;

    if (images.length >= 5) return;
    if (images.length + files.length > 5) {
      limit = 5 - images.length;
    } else {
      limit = files.length;
    }

    for (let i = 0; i < limit; i++) {
      const formData = new FormData();
      formData.append('image', files[i]);
      const res = await PostCommunityImage(formData);
      setImages(prev => [...prev, res.data]);
    }
  };

  const removeImage = (targetId: number) => {
    setImages(prev => prev.filter(({ id }) => id !== targetId));
  };

  const selectBranch = (branch: Branch) => {
    setSelectedBranch(branch);
    setSelectBoxShown(false);
  };

  const submit = async () => {
    if (content === '' && images.length === 0) {
      alert('내용을 입력해주세요.');
      return;
    }

    if (!selectedBranch) {
      alert('지점을 선택해주세요.');
      return;
    }

    const config = makeAuthorizationConfig(cookies.access_token);
    const result = await PostCommunityPost(
      {
        content,
        branch: selectedBranch.id,
        image_list: images.map(({ id }) => id),
      },
      config,
    );

    if (result.status === 201) {
      alert('게시물이 등록되었습니다.');
      window.location.reload();
    } else {
      alert('일시적인 오류로 등록에 실패했습니다.\n다시한번 시도해주세요.');
    }
  };

  useEffect(() => {
    const getDataAndPosition = async () => {
      const { data } = await GetAllBranchesAPI();

      if (!data.results) return;

      if (
        'geolocation' in navigator &&
        'getCurrentPosition' in navigator.geolocation
      ) {
        navigator.geolocation.getCurrentPosition(
          async position => {
            const sortedBranches = data.results
              .map(branch => ({
                ...branch,
                distance: getDistanceFromLatLonInKm(
                  position.coords.latitude,
                  position.coords.longitude,
                  Number(branch.latitude),
                  Number(branch.longitude),
                ),
              }))
              .sort((a, b) => a.distance - b.distance);

            setBranches(sortedBranches);
            setLoading(false);
          },
          async () => {
            setBranches(data.results);
            setLoading(false);
          },
        );
      } else {
        setBranches(data.results);
        setLoading(false);
      }
    };

    getDataAndPosition();
  }, []);

  if (isLoading) {
    return (
      <div className="dimd-layer">
        <div className="inner big-type">
          <section className="cnts-write">
            <Spinner />
            <button type="button" className="btn-close" onClick={closeModal}>
              <span>팝업 닫기</span>
            </button>
          </section>
        </div>
      </div>
    );
  }

  return (
    <div className="dimd-layer">
      <div className="inner big-type">
        <section className="cnts-write">
          <h1>게시물 등록</h1>
          <div className="form">
            <textarea
              placeholder="여러분의 이야기를 들려주세요"
              title="여러분의 이야기를 들려주세요"
              onChange={({ target: { value } }) => setContent(value)}
              value={content}
            />
            <div className="photo-up">
              {images.length > 0 ? (
                <ul>
                  {images.map(({ image, id }) => (
                    <li key={id}>
                      <span style={{ backgroundImage: `url(${image})` }} />
                      <button type="button" onClick={() => removeImage(id)}>
                        <span>사진 삭제</span>
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p>사진을 등록해주세요 (최대 5장까지 가능)</p>
              )}
              <div className="file-up">
                <input
                  type="file"
                  id="photoUp"
                  onClick={checkLimit}
                  onChange={handleImageUpload}
                  multiple
                />
                <label style={{ cursor: 'pointer' }} htmlFor="photoUp">
                  <span>사진등록</span>
                </label>
              </div>
            </div>
            <div className={`cho-spot ${isSelectBoxShown ? 'selected' : ''}`}>
              <button type="button" onClick={() => setSelectBoxShown(v => !v)}>
                {selectedBranch
                  ? selectedBranch.name
                  : '지점을 선택해주세요 (필수)'}
              </button>
              {isSelectBoxShown && (
                <div
                  className="select-w"
                  style={{ maxHeight: 200, overflow: 'auto' }}
                >
                  <ul>
                    {branches.map(branch => (
                      <li
                        style={{ cursor: 'pointer' }}
                        key={branch.name}
                        onClick={() => selectBranch(branch)}
                      >
                        <a>{branch.name}</a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
          <div className="ly-btn">
            <ButtonWrapper>
              <a href="#" onClick={submit}>
                등록
              </a>
            </ButtonWrapper>
          </div>
          <button type="button" className="btn-close" onClick={closeModal}>
            <span>팝업 닫기</span>
          </button>
        </section>
      </div>
    </div>
  );
};

const ButtonWrapper = styled.div`
  flex: 1;
  @media screen and (max-width: 786px) {
    margin-bottom: 100px;
  }
`;

export default ModalAddNewFeed;
